/**
 * Supported tokens on Anvil Protocol (Ethereum Mainnet)
 * Source: https://docs.anvil.xyz/protocol-concepts/collateral-vault/functionality
 */

export interface Token {
  symbol: string
  name: string
  address: `0x${string}`
  decimals: number
  logo?: string
  category: 'stablecoin' | 'btc' | 'eth' | 'lst' // Liquid Staking Token
  isNative?: boolean // True for native ETH (needs wrapping)
  wrapsTo?: string // Symbol of wrapped token (ETH -> WETH)
}

/**
 * Tokens supported by Anvil CollateralVault on Ethereum Mainnet
 * These tokens can be deposited as collateral
 */
export const SUPPORTED_TOKENS: Record<string, Token> = {
  ETH: {
    symbol: 'ETH',
    name: 'Ethereum',
    address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2', // WETH address (for wrapping)
    decimals: 18,
    category: 'eth',
    isNative: true,
    wrapsTo: 'WETH',
  },
  USDC: {
    symbol: 'USDC',
    name: 'USD Coin',
    address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    decimals: 6,
    category: 'stablecoin',
  },
  wBTC: {
    symbol: 'wBTC',
    name: 'Wrapped Bitcoin',
    address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599',
    decimals: 8,
    category: 'btc',
  },
  cbBTC: {
    symbol: 'cbBTC',
    name: 'Coinbase Wrapped BTC',
    address: '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf',
    decimals: 8,
    category: 'btc',
  },
  WETH: {
    symbol: 'WETH',
    name: 'Wrapped Ether',
    address: '0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2',
    decimals: 18,
    category: 'eth',
  },
  wstETH: {
    symbol: 'wstETH',
    name: 'Wrapped stETH',
    address: '0x7f39C581F595B53c5cb19bD0b3f8dA6c935E2Ca0',
    decimals: 18,
    category: 'lst',
  },
}

/**
 * Get token by address
 */
export function getTokenByAddress(address: string): Token | undefined {
  return Object.values(SUPPORTED_TOKENS).find(
    (token) => token.address.toLowerCase() === address.toLowerCase()
  )
}

/**
 * Get token by symbol
 */
export function getTokenBySymbol(symbol: string): Token | undefined {
  return SUPPORTED_TOKENS[symbol.toUpperCase()]
}

/**
 * Get all token addresses
 */
export function getAllTokenAddresses(): `0x${string}`[] {
  return Object.values(SUPPORTED_TOKENS).map((token) => token.address)
}

/**
 * Get tokens by category
 */
export function getTokensByCategory(category: Token['category']): Token[] {
  return Object.values(SUPPORTED_TOKENS).filter((token) => token.category === category)
}

/**
 * Format token amount for display
 */
export function formatTokenAmount(amount: bigint, decimals: number): string {
  const divisor = BigInt(10 ** decimals)
  const whole = amount / divisor
  const fraction = amount % divisor
  
  if (fraction === 0n) {
    return whole.toString()
  }
  
  const fractionStr = fraction.toString().padStart(decimals, '0')
  const trimmed = fractionStr.replace(/0+$/, '')
  
  return `${whole}.${trimmed}`
}

/**
 * Parse token amount from string
 */
export function parseTokenAmount(amount: string, decimals: number): bigint {
  // Guard against empty, negative, or non-numeric input
  const trimmed = amount.trim()
  if (!trimmed || trimmed === '-' || trimmed === '.') return 0n

  const num = Number(trimmed)
  if (isNaN(num) || num < 0) return 0n

  const [whole, fraction = ''] = trimmed.split('.')
  const paddedFraction = fraction.padEnd(decimals, '0').slice(0, decimals)
  const combined = whole + paddedFraction
  try {
    return BigInt(combined)
  } catch {
    return 0n
  }
}

